<template>
  <div class="nf-datepicker"
    :class="{ open, disabled: isDisabled, 'nf-datepicker--form': formSkin }" ref="root">
    <div class="nf-datepicker-field" :class="{ open }">
      <input class="nf-datepicker-input" type="text" inputmode="numeric" maxlength="10"
        ref="input" v-model="text" :placeholder="placeholderText" :disabled="isDisabled"
        @input="onType" @focus="onFocus" @blur="onBlur" @mouseup="onMouseUp"
        @keydown.enter.prevent="$event.target.blur()" />
      <span class="nf-datepicker-icon" @mousedown.prevent="toggle">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </span>
    </div>
    <!-- mousedown.prevent keeps the input focused while interacting with the
         popup, so consumers saving on blur (studio editors) don't fire early -->
    <div v-if="open" ref="pop" class="nf-datepicker-pop"
      :class="{ 'nf-datepicker-pop--right': alignRight }" @mousedown.prevent>
      <MiniCalendar :initial-date="initialDate" :selected-date="selectedDate" @date-selected="onPick" />
    </div>
  </div>
</template>

<script>
import MiniCalendar from '@/views/tools/calendar/MiniCalendar.vue'

// Typeable date field with the Calendar section's MiniCalendar as its popup.
// Replaces both antd's calendar (Matt: ugly, 7/7) and native <input type="date">
// (Chrome's picker popup swallows typed digits, and a typed 2-digit year commits
// literally as year 00xx). Model is a "YYYY-MM-DD" string.
//
// Works two ways:
//  - standalone: v-model="someIsoString"
//  - as a Vue Formulate custom type: <FormulateInput type="nfdate" .../> — VF
//    passes `context`; model/disabled/placeholder then come from the context.
export default {
  name: 'NfDatePicker',
  components: { MiniCalendar },
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: 'mm/dd/yyyy' },
    disabled: { type: Boolean, default: false },
    context: { type: Object, default: null },
    // YYYY-MM-DD month to open the calendar on while the field is empty, like
    // antd's defaultPickerValue: an end date opens on its start date's month
    // instead of today's.
    defaultPickerValue: { type: String, default: '' },
  },
  data() {
    return { open: false, focused: false, text: '', selectOnUp: false, alignRight: false }
  },
  created() {
    this.text = this.displayValue
  },
  computed: {
    modelValue() {
      return this.context ? this.context.model : this.value
    },
    isDisabled() {
      if (this.context) return !!this.context.attributes.disabled
      return this.disabled
    },
    placeholderText() {
      if (this.context && this.context.attributes.placeholder) {
        return this.context.attributes.placeholder
      }
      return this.placeholder
    },
    // Inside VF the field normally wears the app's generic input skin so it
    // matches its form siblings (employee drawer etc.). Pass the `nf-chrome`
    // attribute to keep the component's own wizard-style chrome instead
    // (rounded field, icon inside) — used where the page design calls for the
    // specialized-wizard look, e.g. the telematics report panel.
    formSkin() {
      if (!this.context) return false
      const attrs = this.context.attributes || {}
      return !('nf-chrome' in attrs || 'nfChrome' in attrs)
    },
    // Parse "YYYY-MM-DD" as a LOCAL date (not `new Date(str)`, which is UTC and
    // shifts a day in negative offsets).
    parsedDate() {
      if (!this.modelValue) return null
      const [y, m, d] = String(this.modelValue).split('-').map(Number)
      if (!y || !m || !d) return null
      return new Date(y, m - 1, d)
    },
    defaultPickerDate() {
      if (!this.defaultPickerValue) return null
      const [y, m, d] = String(this.defaultPickerValue).split('-').map(Number)
      if (!y || !m || !d) return null
      return new Date(y, m - 1, d)
    },
    initialDate() {
      return this.parsedDate || this.defaultPickerDate || new Date()
    },
    // MiniCalendar highlights whatever selectedDate matches; an invalid date means
    // nothing is highlighted when the field is empty (it doesn't guard null).
    selectedDate() {
      return this.parsedDate || new Date(NaN)
    },
    displayValue() {
      const dt = this.parsedDate
      if (!dt) return ''
      const mm = String(dt.getMonth() + 1).padStart(2, '0')
      const dd = String(dt.getDate()).padStart(2, '0')
      return `${mm}/${dd}/${dt.getFullYear()}`
    },
  },
  watch: {
    // Keep the text field in sync with external value changes (calendar pick,
    // prefill) — but not while the user is typing, or we'd clobber their input.
    modelValue() {
      if (!this.focused) this.text = this.displayValue
    },
    open(isOpen) {
      if (isOpen) {
        document.addEventListener('mousedown', this.onOutside)
        this.$nextTick(this.placePop)
      } else {
        document.removeEventListener('mousedown', this.onOutside)
        this.alignRight = false
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside)
  },
  methods: {
    commit(iso) {
      if (this.context) {
        this.context.model = iso
      } else {
        this.$emit('input', iso)
      }
    },
    toggle() {
      if (this.isDisabled) return
      this.open = !this.open
    },
    onFocus(e) {
      this.focused = true
      this.open = true
      // Select the whole value so typing over a prefilled field replaces it.
      // Without this, maxlength blocks every keystroke once the field is full
      // ("it's not typeable").
      this.selectOnUp = true
      e.target.select()
    },
    onMouseUp(e) {
      // The click's own mouseup would collapse the focus-select to a caret;
      // suppress it exactly once so the selection survives.
      if (this.selectOnUp) {
        e.preventDefault()
        this.selectOnUp = false
      }
    },
    onBlur() {
      this.focused = false
      this.selectOnUp = false
      // Focus truly left (popup clicks prevent blur), so the popup can close.
      this.open = false
      // A 2-digit year is a valid entry on commit: "06/30/27" means 2027
      // (00-49 → 20xx, 50-99 → 19xx). Expand and commit it here rather than
      // silently dropping what the user typed.
      const iso = this.parseText(this.expandShortYear(this.text))
      if (iso) {
        if (iso !== this.modelValue) this.commit(iso)
        // Don't wait for the prop round-trip; show the canonical form now.
        const [y, m, d] = iso.split('-')
        this.text = `${m}/${d}/${y}`
      } else {
        // Snap back to the committed value's canonical MM/DD/YYYY, dropping any
        // half-typed / invalid entry.
        this.text = this.displayValue
      }
      // Let Vue Formulate run its blur-driven validation display.
      if (this.context && typeof this.context.blurHandler === 'function') {
        this.context.blurHandler()
      }
    },
    expandShortYear(str) {
      const m = String(str || '').match(/^(\d{2}\/\d{2}\/)(\d{2})$/)
      if (!m) return str
      const yy = Number(m[2])
      return m[1] + (yy < 50 ? 2000 + yy : 1900 + yy)
    },
    onType() {
      // Auto-mask: user types digits only (mmddyyyy); we insert the slashes AND
      // smart-pad — a month digit >1 (e.g. 5) becomes "05", a day digit >3
      // becomes "0d", so "5" → "05" and "55" → "05/05" (Matt 7/8).
      const digits = String(this.text || '').replace(/\D/g, '')
      const masked = this.maskDigits(digits)
      this.text = masked
      if (digits.length === 0) {
        this.commit('')
        return
      }
      const iso = this.parseText(masked)
      if (iso) this.commit(iso)
    },
    // Cleave-style date mask. Consumes the digit run left-to-right, auto-padding
    // an impossible leading month/day digit to two digits and inserting slashes
    // only once the next segment actually has a digit (no trailing slash).
    maskDigits(raw) {
      const digits = raw.slice(0, 8)
      let idx = 0

      let mm = ''
      if (idx < digits.length) {
        const a = digits[idx]
        if (a >= '2') {
          mm = '0' + a // 2-9 → 02..09, month done
          idx += 1
        } else if (idx + 1 < digits.length) {
          const b = digits[idx + 1]
          if (a === '0') {
            mm = a + b // 00..09
            idx += 2
          } else if (b <= '2') {
            mm = a + b // 10,11,12
            idx += 2
          } else {
            mm = '01' // 1 then >2 → 01, the digit belongs to the day
            idx += 1
          }
        } else {
          mm = a // single 0/1 typed so far
          idx += 1
        }
      }

      let dd = ''
      if (mm.length === 2 && idx < digits.length) {
        const a = digits[idx]
        if (a >= '4') {
          dd = '0' + a // 4-9 → 04..09, day done
          idx += 1
        } else if (idx + 1 < digits.length) {
          const b = digits[idx + 1]
          if (a === '3' && b > '1') {
            dd = '03' // 3 then >1 → 03, the digit belongs to the year
            idx += 1
          } else {
            dd = a + b // 00..31
            idx += 2
          }
        } else {
          dd = a // single 0-3 typed so far
          idx += 1
        }
      }

      let yyyy = ''
      if (dd.length === 2 && idx < digits.length) {
        yyyy = digits.slice(idx, idx + 4)
      }

      let out = mm
      if (mm.length === 2 && dd.length > 0) {
        out += '/' + dd
        if (dd.length === 2 && yyyy.length > 0) {
          out += '/' + yyyy
        }
      }
      return out
    },
    // Parse a manually typed MM/DD/YYYY into the "YYYY-MM-DD" model string; returns
    // null for partial/invalid input (incl. impossible dates like 02/31) so typing
    // in progress never emits garbage.
    parseText(str) {
      const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
      if (!m) return null
      const mm = Number(m[1])
      const dd = Number(m[2])
      const yyyy = Number(m[3])
      // Reject implausible years so a pasted ISO date ("2026-07-08" → masked as
      // 02/02/6070) or a typo like 0999 never commits a garbage year to the model.
      if (yyyy < 1900 || yyyy > 2100) return null
      const dt = new Date(yyyy, mm - 1, dd)
      if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) {
        return null
      }
      return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
    },
    onPick(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      this.commit(`${y}-${m}-${d}`)
      // Sync the text now (the modelValue watcher skips updates while focused)
      // and blur so blur-driven consumers get exactly one event, after commit.
      this.text = `${m}/${d}/${y}`
      this.open = false
      if (this.$refs.input && document.activeElement === this.$refs.input) {
        this.$refs.input.blur()
      } else if (this.context && typeof this.context.blurHandler === 'function') {
        // Picked via the icon without ever focusing the input: still notify
        // blur-context listeners (studio editors save on it).
        this.context.blurHandler()
      }
    },
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) {
        this.open = false
      }
    },
    // The popup hangs left-aligned under the field. A field in the right half of
    // a narrow panel (the wizard's End Date) pushes it past the panel's clipping
    // edge and hides Friday and Saturday, so align it to the field's right edge
    // instead when that fits - antd's adjustX, for the one direction we need.
    placePop() {
      const pop = this.$refs.pop
      const root = this.$refs.root
      if (!pop || !root) return
      let right = window.innerWidth
      let left = 0
      for (let el = root.parentElement; el && el !== document.body; el = el.parentElement) {
        if (getComputedStyle(el).overflowX !== 'visible') {
          const r = el.getBoundingClientRect()
          right = Math.min(right, r.left + el.clientLeft + el.clientWidth)
          left = Math.max(left, r.left + el.clientLeft)
          break
        }
      }
      const popRect = pop.getBoundingClientRect()
      const fieldRight = root.getBoundingClientRect().right
      this.alignRight = popRect.right > right && fieldRight - popRect.width >= left
    },
  },
}
</script>

<style lang="scss" scoped>
.nf-datepicker {
  position: relative;
  width: 100%;
}

.nf-datepicker-field {
  height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d9dee2;
  border-radius: 8px;
  padding: 0 12px;
  background: #fff;
  box-sizing: border-box;

  &.open {
    border-color: #0b3a4c;
  }
}

.nf-datepicker.disabled .nf-datepicker-field {
  background: #f5f7fa;
}

.nf-datepicker-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #273b4a;
  padding: 0;

  &::placeholder {
    color: #9aa5b1;
  }

  &:disabled {
    color: #9aa5b1;
    cursor: not-allowed;
  }
}

.nf-datepicker-icon {
  flex: 0 0 auto;
  color: #687681;
  cursor: pointer;
  display: inline-flex;
}

// When the field keeps its OWN chrome (standalone, or `nf-chrome` inside VF),
// the app's global input rules must not restyle the inner input — they carry
// !important, so these resets must outgun them on specificity AND importance.
.nf-datepicker:not(.nf-datepicker--form) .nf-datepicker-field .nf-datepicker-input {
  border: none !important;
  border-radius: 0 !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 14px !important;
  color: #273b4a !important;
}

// Inside a Vue Formulate form the app's global input styles reach the inner
// <input> anyway (border, radius, height, padding — same rules as sibling
// FormulateInputs). Rather than fight the cascade, drop our own field chrome
// there and let the input wear the app's standard skin, so the field looks
// pixel-identical to its neighbors; the calendar icon overlays the right edge.
.nf-datepicker--form {
  .nf-datepicker-field {
    border: none;
    border-radius: 0;
    height: auto;
    padding: 0;
    background: transparent;
    position: relative;
  }

  .nf-datepicker-input {
    padding-right: 36px !important;
  }

  .nf-datepicker-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.nf-datepicker--form.disabled .nf-datepicker-field {
  background: transparent;
}

.nf-datepicker-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 50;
  width: 260px;
  background: #fff;
  border: 1px solid #d9dee2;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(21, 47, 74, 0.12);
  padding: 8px;

  &--right {
    left: auto;
    right: 0;
  }

  // MiniCalendar ships its own border + bottom margin for its in-sidebar use;
  // inside this popover the popover provides the chrome, so strip them.
  ::v-deep .mini-calendar {
    margin-bottom: 0;
    border: none;
    padding: 4px;
  }
}
</style>
